import React, { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  Building2, 
  Users, 
  LogOut, 
  Home, 
  BarChart3,
  Shield,
  Key,
  Mail,
  Menu
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useOrganization } from "@/contexts/OrganizationContext";
import { Navigate } from "react-router-dom";
import { SuperAdminSwitcher } from "@/components/SuperAdminSwitcher";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import UserMenu from "@/components/UserMenu";

// Light theme CSS variables to apply globally for super-admin
const lightThemeVars: Record<string, string> = {
  '--background': '0 0% 98%',
  '--foreground': '222.2 47.4% 11.2%',
  '--card': '0 0% 100%',
  '--card-foreground': '222.2 47.4% 11.2%',
  '--popover': '0 0% 100%',
  '--popover-foreground': '222.2 47.4% 11.2%',
  '--primary': '199 89% 48%',
  '--primary-foreground': '210 40% 98%',
  '--secondary': '210 40% 96.1%',
  '--secondary-foreground': '222.2 47.4% 11.2%',
  '--muted': '210 40% 96.1%',
  '--muted-foreground': '215.4 16.3% 46.9%',
  '--accent': '210 40% 96.1%',
  '--accent-foreground': '222.2 47.4% 11.2%',
  '--destructive': '0 84.2% 60.2%',
  '--destructive-foreground': '210 40% 98%',
  '--border': '214.3 31.8% 91.4%',
  '--input': '214.3 31.8% 91.4%',
  '--ring': '199 89% 48%',
};

const SuperAdminLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isSuperAdmin, isLoading } = useOrganization();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Apply light theme globally when in super-admin
  useEffect(() => {
    const root = document.documentElement;
    const originalValues: Record<string, string> = {};
    
    // Store original values and apply light theme
    Object.entries(lightThemeVars).forEach(([key, value]) => {
      originalValues[key] = root.style.getPropertyValue(key);
      root.style.setProperty(key, value);
    });

    // Cleanup: restore original values on unmount
    return () => {
      Object.entries(originalValues).forEach(([key, value]) => {
        if (value) {
          root.style.setProperty(key, value);
        } else {
          root.style.removeProperty(key);
        }
      });
    };
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) =>
    location.pathname === path || 
    (path !== "/super-admin" && location.pathname.startsWith(path));

  const linkClass = (path: string) =>
    cn(
      "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
      isActive(path) 
        ? "bg-sky-100 text-sky-700" 
        : "text-slate-600 hover:bg-slate-100"
    );

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error("Failed to log out");
    } else {
      toast.success("Logged out successfully");
      navigate("/login");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isSuperAdmin) {
    return <Navigate to="/admin" replace />;
  }

  // Extracted sidebar content for reuse in desktop and mobile
  const SidebarContent = () => (
    <>
      <div className="h-16 flex items-center px-6 border-b border-slate-200 gap-2">
        <Shield className="h-5 w-5 text-sky-600" />
        <span className="text-lg font-bold text-slate-800">Super Admin</span>
      </div>
      <nav className="flex-grow p-4 space-y-1 text-sm font-medium overflow-y-auto">
        <Link to="/super-admin" className={linkClass("/super-admin") + (location.pathname !== "/super-admin" ? " text-slate-600 hover:bg-slate-100" : "")}>
          <LayoutDashboard size={18} /> Dashboard
        </Link>

        <Link to="/super-admin/organizations" className={linkClass("/super-admin/organizations")}>
          <Building2 size={18} /> Organizations
        </Link>

        <Link to="/super-admin/users" className={linkClass("/super-admin/users")}>
          <Users size={18} /> All Users
        </Link>

        <Link to="/super-admin/integrations" className={linkClass("/super-admin/integrations")}>
          <Key size={18} /> Platform Integrations
        </Link>

        <Link to="/super-admin/analytics" className={linkClass("/super-admin/analytics")}>
          <BarChart3 size={18} /> Cross-Org Analytics
        </Link>

        <Link to="/super-admin/contacts" className={linkClass("/super-admin/contacts")}>
          <Mail size={18} /> All Contacts
        </Link>
      </nav>
      <div className="p-4 border-t border-slate-200 space-y-1">
        <Link to="/admin" className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-100">
          <Building2 size={18} /> Org Admin
        </Link>
        <Link to="/" className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-100">
          <Home size={18} /> Return to Site
        </Link>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600"
        >
          <LogOut size={18} /> Log Out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <SuperAdminSwitcher />
      <div className="flex flex-grow">
        {/* Desktop Sidebar - hidden on mobile/tablet */}
        <div className="hidden lg:flex w-64 bg-white border-r border-slate-200 flex-col">
          <SidebarContent />
        </div>

        {/* Mobile Sheet Sidebar */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetContent side="left" className="w-72 p-0 bg-white flex flex-col">
            <SidebarContent />
          </SheetContent>
        </Sheet>

        {/* Main content area - full width on mobile */}
        <div className="flex-grow flex flex-col overflow-hidden bg-slate-50">
          {/* Super Admin Header with hamburger menu */}
          <header className="h-14 border-b border-slate-200 bg-white flex items-center justify-between px-4 lg:px-6 sticky top-0 z-10">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setMobileMenuOpen(true)} 
                className="lg:hidden text-slate-600 hover:bg-slate-100"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </div>
            <UserMenu />
          </header>
          <div className="flex-grow overflow-auto">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminLayout;
